import { type DestinationListItem, type DestinationListQuery } from '@utazzo/shared';
import { UzStatusBadge } from '@utazzo/ui';
import React, { useMemo } from 'react';

import { DestinationFormDrawer } from '../components/DestinationFormDrawer';
import { listDestinations } from '../destinations.api';
import {
  useBulkDeleteDestinations,
  useBulkSetDestinationsStatus,
  useDeleteDestination,
  useDestinationsList,
} from '../use-destinations';

import { type DataGridColumn } from '@/components/data-grid';
import { ListScreen, type ListScreenColumns } from '@/components/list-screen';
import { useCurrentUser } from '@/features/auth/use-auth';

const MIN_SEARCH_LENGTH = 2;

export const DestinationsListPage = (): React.JSX.Element => {
  const me = useCurrentUser();
  const canWrite =
    me?.role === 'ADMIN' ||
    me?.role === 'OPS' ||
    me?.role === 'WEBSITE_MANAGER' ||
    me?.role === 'WEBSITE_SEO';
  const deleteDestination = useDeleteDestination();
  const bulkDeleteMut = useBulkDeleteDestinations();
  const bulkSetStatusMut = useBulkSetDestinationsStatus();

  const columns: ListScreenColumns<DestinationListItem> = ({ state }) => {
    const cols: ReadonlyArray<DataGridColumn<DestinationListItem>> = [
      {
        id: 'serial',
        header: 'S.No',
        width: '64px',
        cell: (_row, index) => (
          <span className="text-zinc-500">{(state.page - 1) * state.pageSize + index + 1}.</span>
        ),
      },
      {
        id: 'name',
        header: 'Name',
        sortable: true,
        cell: (row) => <span className="font-medium text-zinc-900">{row.name}</span>,
      },
      // Region column was hidden 2026-06-24 along with the form field
      // (every destination ties to a country directly now). The
      // server still ships `region` on the row for back-compat with
      // any saved view exports; the grid just doesn't render it.
      {
        id: 'country',
        header: 'Country',
        cell: (row) => {
          if (!row.country) return <span className="text-zinc-400">-</span>;
          return (
            <span className={row.country.isActive ? 'text-zinc-600' : 'text-zinc-400 italic'}>
              {row.country.name}
              {!row.country.isActive && ' (inactive)'}
            </span>
          );
        },
      },
      {
        id: 'state',
        header: 'State',
        cell: (row) => <span className="text-zinc-600">{row.state?.name ?? '-'}</span>,
      },
      {
        id: 'city',
        header: 'City',
        cell: (row) => <span className="text-zinc-600">{row.city?.name ?? '-'}</span>,
      },
      {
        id: 'scope',
        header: 'Scope',
        width: '120px',
        cell: (row) => (
          <span
            className={
              'inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ring-inset ' +
              (row.isInternational
                ? 'bg-indigo-50 text-indigo-700 ring-indigo-200'
                : 'bg-emerald-50 text-emerald-700 ring-emerald-200')
            }
          >
            {row.isInternational ? 'International' : 'Domestic'}
          </span>
        ),
      },
      {
        id: 'haul',
        header: 'Haul',
        width: '100px',
        cell: (row) =>
          row.haul ? (
            <span className="text-zinc-600">
              {row.haul === 'LONG_HAUL' ? 'Long Haul' : 'Short Haul'}
            </span>
          ) : (
            <span className="text-zinc-400">-</span>
          ),
      },
      {
        id: 'tripTypes',
        header: 'Trip types',
        cell: (row) =>
          row.tripTypes.length === 0 ? (
            <span className="text-zinc-400">-</span>
          ) : (
            <div className="flex flex-wrap gap-1">
              {row.tripTypes.map((t) => (
                <span
                  key={t.id}
                  className="rounded bg-zinc-100 px-1.5 py-0.5 text-[11px] font-medium text-zinc-700"
                >
                  {t.name}
                </span>
              ))}
            </div>
          ),
      },
      {
        id: 'sortOrder',
        header: 'Sort',
        width: '80px',
        sortable: true,
        cell: (row) => <span className="text-zinc-500">{row.sortOrder}</span>,
      },
      {
        id: 'status',
        header: 'Status',
        cell: (row) => <UzStatusBadge status={row.isActive ? 'active' : 'inactive'} />,
      },
    ];
    return cols;
  };

  const toQuery = useMemo(
    () =>
      (
        state: {
          filters: Record<string, string | undefined>;
        },
        base: Partial<DestinationListQuery>,
      ): DestinationListQuery => {
        const status = state.filters.status;
        return {
          ...(base as DestinationListQuery),
          ...(status === 'active' ? { isActive: true } : {}),
          ...(status === 'inactive' ? { isActive: false } : {}),
        };
      },
    [],
  );

  return (
    <ListScreen<DestinationListItem, DestinationListQuery>
      title="Destinations"
      description="Pre-canned travel targets shown to operators on the lead-capture form. Inactive destinations and ones whose country is inactive are hidden from the picker."
      useList={useDestinationsList}
      defaultSort={{ by: 'sortOrder', dir: 'asc' }}
      minSearchLength={MIN_SEARCH_LENGTH}
      toQuery={toQuery}
      columns={columns}
      filters={[
        {
          id: 'status',
          label: 'Status',
          options: [
            { value: '', label: 'Active + inactive' },
            { value: 'active', label: 'Active only' },
            { value: 'inactive', label: 'Inactive only' },
          ],
          widthClass: 'w-48',
        },
      ]}
      getRowId={(row) => row.id}
      bulkActions={
        canWrite
          ? {
              delete: {
                onDelete: async (rows) => {
                  await bulkDeleteMut.mutateAsync(rows.map((r) => r.id));
                },
                confirm: (rows) => ({
                  title: `Delete ${rows.length} destination${rows.length === 1 ? '' : 's'}?`,
                  description:
                    'These destinations will be soft-deleted and hidden from the picker.',
                }),
              },
              setActive: {
                onSetActive: async (rows, isActive) =>
                  bulkSetStatusMut.mutateAsync({ ids: rows.map((r) => r.id), isActive }),
                confirm: (rows, isActive) => ({
                  title: `${isActive ? 'Activate' : 'Deactivate'} ${rows.length} destination${rows.length === 1 ? '' : 's'}?`,
                  description: isActive
                    ? 'They will become selectable again in the lead-capture picker.'
                    : 'They will be hidden from new selections but kept on existing leads.',
                }),
              },
            }
          : undefined
      }
      export={{
        fetchPage: (q) => listDestinations(q),
        filename: 'destinations',
        csv: [
          { header: 'Name', value: (r) => r.name },
          { header: 'Country', value: (r) => r.country?.name ?? '' },
          { header: 'State', value: (r) => r.state?.name ?? '' },
          { header: 'City', value: (r) => r.city?.name ?? '' },
          { header: 'Sort order', value: (r) => r.sortOrder },
          { header: 'Status', value: (r) => (r.isActive ? 'Active' : 'Inactive') },
        ],
      }}
      createButton={canWrite ? { label: 'Add destination' } : undefined}
      renderDrawer={({ open, mode, row, close }) => (
        <DestinationFormDrawer
          open={open}
          onOpenChange={(o) => {
            if (!o) close();
          }}
          mode={mode}
          destination={row}
        />
      )}
      rowActions={{
        edit: canWrite,
        delete: canWrite
          ? {
              onDelete: (row) => deleteDestination.mutateAsync(row.id),
              confirm: (row) => ({
                title: 'Delete this destination?',
                description: `${row.name} will be soft-deleted and hidden from the picker. Leads already tagged with this destination keep the reference until you clear it.`,
              }),
            }
          : undefined,
      }}
      copy={{
        searchPlaceholder: 'Search by name (min 2 characters)',
        emptyTitle: 'No destinations match these filters',
        emptyDescription: 'Adjust the status filter or clear the search box.',
        errorTitle: 'Failed to load destinations',
        errorDescription: 'The server returned an error. Try refreshing or check your network.',
      }}
    />
  );
};
